import { CheckCircle, XCircle, AlertCircle } from "lucide-react";

export const ZeroTrustDmarcImplementation = () => {
  const steps = [
    {
      step: 1,
      title: "Audit All Sending Sources",
      description: "Identify every service, platform, and application that sends email on behalf of your domain. Include marketing tools, CRM, transactional email, help desk, and internal systems.",
      checklist: [
        "List all marketing platforms (Mailchimp, HubSpot, etc.)",
        "Identify transactional senders (Stripe, AWS SES, etc.)",
        "Document internal applications that send email",
        "Check for shadow IT sending services",
      ],
    },
    {
      step: 2,
      title: "Configure SPF for All Sources",
      description: "Update your SPF record to include all legitimate sending sources. Ensure you don't exceed the 10 DNS lookup limit—use SPF flattening if necessary.",
      checklist: [
        "Add all authorized senders to SPF record",
        "Use 'include:' for third-party services",
        "Verify lookup count stays under 10",
        "Test with SPF validation tools",
      ],
    },
    {
      step: 3,
      title: "Implement DKIM for Every Source",
      description: "Generate DKIM keys for each sending service and publish the public keys in DNS. Ensure all services sign outgoing emails with DKIM.",
      checklist: [
        "Generate unique DKIM keys per service",
        "Publish DKIM public keys in DNS",
        "Verify DKIM signatures pass validation",
        "Use 2048-bit keys minimum",
      ],
    },
    {
      step: 4,
      title: "Deploy DMARC with Monitoring",
      description: "Start with p=none to collect reports without affecting delivery. Analyze DMARC reports to identify authentication failures before enforcing.",
      checklist: [
        "Publish DMARC record with p=none",
        "Configure rua= for aggregate reports",
        "Set up DMARC report analyzer",
        "Review reports for 2-4 weeks",
      ],
    },
    {
      step: 5,
      title: "Move to Quarantine",
      description: "Once all legitimate sources pass authentication, update policy to p=quarantine. This sends failing emails to spam without rejecting them.",
      checklist: [
        "Verify 100% of legitimate email passes",
        "Update DMARC to p=quarantine; pct=25",
        "Gradually increase pct to 100",
        "Monitor for delivery issues",
      ],
    },
    {
      step: 6,
      title: "Enforce with p=reject",
      description: "The final step: reject all emails that fail DMARC. This is the 2026 requirement. Non-compliant emails will be rejected, not delivered.",
      checklist: [
        "Confirm zero false positives in reports",
        "Update DMARC to p=reject",
        "Set up alerts for policy failures",
        "Document rollback procedures",
      ],
    },
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        Implementation Roadmap
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        Follow this step-by-step guide to achieve DMARC compliance with p=reject. 
        Rushing can break legitimate email delivery—take time at each stage.
      </p>

      <div className="space-y-6">
        {steps.map((item) => (
          <div
            key={item.step}
            className="bg-card rounded-xl border border-border p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold shrink-0">
                {item.step}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {item.checklist.map((check, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <CheckCircle className="w-4 h-4 text-success shrink-0" />
                      {check}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};